/*
  Header "Drawer" mode on desktop: <header-hover-drawer>.

  The header keeps its inline links. Links flagged data-hover-drawer-trigger
  slide the existing menu drawer (the one the mobile menu icon opens) out from
  the left on hover, so one drawer and one set of menus serve both.

  Opening is handled here rather than through Dawn's HeaderDrawer: that locks
  page scroll (the scrollbar disappears and the page jumps sideways) and moves
  focus into the drawer, neither of which a hover should do.

  - Hover (with a short intent delay, so brushing past doesn't open it) opens;
    leaving the link and the drawer closes it after a grace period that covers
    the gap between the two.
  - Clicking the link still goes to its page. On a touch screen the first tap
    opens the drawer and a second tap follows the link.
  - Arrow Down on the link opens the drawer and moves focus into it; Escape
    closes it and returns focus to the link.
*/
if (!customElements.get('header-hover-drawer')) {
  customElements.define(
    'header-hover-drawer',
    class HeaderHoverDrawer extends HTMLElement {
      OPEN_DELAY = 180;
      CLOSE_DELAY = 280;
      TRANSITION = 400;

      connectedCallback() {
        this.header = this.closest('.header');
        this.section = this.closest('.section-header') || document.querySelector('.section-header');
        this.details = this.header?.querySelector('header-drawer details');
        this.panel = this.details?.querySelector('.menu-drawer');
        this.triggers = [...(this.header?.querySelectorAll('[data-hover-drawer-trigger]') || [])];
        if (!this.details || !this.panel || !this.triggers.length) return;

        this.desktop = window.matchMedia('(min-width: 990px)');
        this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.isOpen = false;

        this.triggers.forEach((trigger) => {
          trigger.addEventListener('mouseenter', () => this.onTriggerEnter(trigger));
          trigger.addEventListener('mouseleave', () => this.onLeave());
          trigger.addEventListener('click', (event) => this.onTriggerClick(event, trigger));
          trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event, trigger));
        });

        this.panel.addEventListener('mouseenter', () => this.cancelClose());
        this.panel.addEventListener('mouseleave', () => this.onLeave());
        this.panel.addEventListener('focusout', (event) => this.onFocusOut(event));

        // The dimmed backdrop is the <details> itself (::after), so a click
        // that lands on it rather than inside the panel closes the drawer.
        this.details.addEventListener('click', (event) => {
          if (this.isOpen && event.target === this.details) this.close();
        });

        this.onKeydown = (event) => {
          if (event.key !== 'Escape' || !this.isOpen) return;
          const focusWasInside = this.panel.contains(document.activeElement);
          this.close();
          if (focusWasInside) this.activeTrigger?.focus();
        };
        this.onScroll = () => this.isOpen && this.updateOffset();
        this.onBreakpoint = () => !this.desktop.matches && this.close(true);

        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.desktop.addEventListener('change', this.onBreakpoint);
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('scroll', this.onScroll);
        this.desktop?.removeEventListener('change', this.onBreakpoint);
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
        clearTimeout(this.removeTimer);
      }

      /* ---- Pointer ------------------------------------------------------- */

      onTriggerEnter(trigger) {
        if (!this.desktop.matches || !this.canHover.matches) return;
        this.cancelClose();
        if (this.isOpen) {
          this.setActive(trigger);
          return;
        }
        clearTimeout(this.openTimer);
        this.openTimer = setTimeout(() => this.open(trigger), this.OPEN_DELAY);
      }

      onLeave() {
        clearTimeout(this.openTimer);
        if (this.isOpen) this.scheduleClose();
      }

      scheduleClose() {
        this.cancelClose();
        this.closeTimer = setTimeout(() => this.close(), this.CLOSE_DELAY);
      }

      cancelClose() {
        clearTimeout(this.closeTimer);
      }

      onTriggerClick(event, trigger) {
        // With a mouse the link just goes to its page. A touch screen at
        // desktop width has no hover, so the first tap opens the drawer.
        if (!this.desktop.matches || this.canHover.matches || this.isOpen) return;
        event.preventDefault();
        this.open(trigger);
      }

      /* ---- Keyboard ------------------------------------------------------ */

      onTriggerKeydown(event, trigger) {
        if (event.key !== 'ArrowDown' || !this.desktop.matches) return;
        event.preventDefault();
        this.open(trigger, { focus: true });
      }

      onFocusOut(event) {
        const next = event.relatedTarget;
        if (!this.isOpen || !next) return;
        if (this.panel.contains(next) || this.triggers.includes(next)) return;
        this.close();
      }

      /* ---- State --------------------------------------------------------- */

      open(trigger, { focus = false } = {}) {
        if (!this.desktop.matches) return;
        clearTimeout(this.removeTimer);
        this.updateOffset();
        this.isOpen = true;
        this.setActive(trigger);

        this.details.setAttribute('open', '');
        this.section?.classList.add('menu-open');
        this.header.classList.add('header--hover-drawer-open');
        // A frame later, so the slide-in transition starts from closed. Focus
        // waits one more frame: until the panel is visible it can't take focus.
        requestAnimationFrame(() => {
          this.details.classList.add('menu-opening');
          if (focus) requestAnimationFrame(() => this.panel.querySelector('a[href], summary, button')?.focus());
        });
      }

      close(immediate = false) {
        clearTimeout(this.openTimer);
        this.cancelClose();
        if (!this.details.hasAttribute('open')) return;

        this.isOpen = false;
        this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        this.details.classList.remove('menu-opening');
        this.section?.classList.remove('menu-open');
        this.header.classList.remove('header--hover-drawer-open');

        // Any submenu opened inside the drawer resets with it.
        this.details.querySelectorAll('details[open]').forEach((submenu) => {
          submenu.removeAttribute('open');
          submenu.classList.remove('menu-opening');
        });
        this.details.querySelectorAll('.submenu-open').forEach((menu) => menu.classList.remove('submenu-open'));

        // [open] stays until the slide-out has finished, or it would vanish
        // instantly instead of animating.
        clearTimeout(this.removeTimer);
        this.removeTimer = setTimeout(
          () => {
            if (!this.isOpen) this.details.removeAttribute('open');
          },
          immediate ? 0 : this.TRANSITION
        );
      }

      setActive(trigger) {
        this.activeTrigger = trigger;
        this.triggers.forEach((item) => item.setAttribute('aria-expanded', String(item === trigger)));
      }

      updateOffset() {
        if (!this.section) return;
        const bottom = Math.max(0, Math.round(this.section.getBoundingClientRect().bottom));
        document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
      }
    }
  );
}
